import { merge } from "es-toolkit";
import { mergeAssets, mergeCustomers, mergeIncome, mergeLiabilities, } from "../merge-dossier-data.js";
import { mapEmailToContactDetails, mapIbanToOtherDetails, mapPhoneNumberToContactDetails, } from "./mappers/contact-mapper.js";
import { mapIdinToCustomer, mapMyNlGovernmentToCustomer, } from "./mappers/government-mapper.js";
import { mapIdCardToCustomer, mapPassportToCustomer, } from "./mappers/identity-mapper.js";
import { mapIblIncomeToIncome, mapIncomeDocumentToIncome, } from "./mappers/income-mapper.js";
import { mapPensionDocumentToIncome } from "./mappers/pension-mapper.js";
import { mapDuoToLiabilities } from "./mappers/student-debt-mapper.js";
import { mapPreFilledTaxInfoToAssets, mapPreFilledTaxInfoToIncome, mapPreFilledTaxInfoToLiabilities, } from "./mappers/tax-info-mapper.js";
export class DatakeeperMapper {
    mapToCustomer(documents, customerId) {
        const errors = [];
        const warnings = [];
        let customer = {
            id: customerId,
            personDetails: {},
            contactDetails: {},
            otherDetails: {},
            residence: {},
            identification: {},
        };
        if (documents.passport) {
            const result = mapPassportToCustomer(documents.passport);
            if (result.customer) {
                customer = merge(customer, result.customer);
            }
            errors.push(...result.errors);
            warnings.push(...result.warnings);
        }
        if (documents["id-card"]) {
            const result = mapIdCardToCustomer(documents["id-card"]);
            if (result.customer) {
                customer = merge(customer, result.customer);
            }
            errors.push(...result.errors);
            warnings.push(...result.warnings);
        }
        if (documents.myNlGovernment) {
            const result = mapMyNlGovernmentToCustomer(documents.myNlGovernment);
            if (result.customer) {
                customer = merge(customer, result.customer);
            }
            errors.push(...result.errors);
            warnings.push(...result.warnings);
        }
        if (documents.idin) {
            const result = mapIdinToCustomer(documents.idin);
            if (result.customer) {
                customer = merge(customer, result.customer);
            }
            errors.push(...result.errors);
            warnings.push(...result.warnings);
        }
        if (documents.email) {
            const result = mapEmailToContactDetails(documents.email);
            customer = merge(customer, { contactDetails: result.contactDetails });
            errors.push(...result.errors);
            warnings.push(...result.warnings);
        }
        if (documents["phone-number"]) {
            const result = mapPhoneNumberToContactDetails(documents["phone-number"]);
            customer = merge(customer, { contactDetails: result.contactDetails });
            errors.push(...result.errors);
            warnings.push(...result.warnings);
        }
        if (documents.iban) {
            const result = mapIbanToOtherDetails(documents.iban);
            customer = merge(customer, { otherDetails: result.otherDetails });
            errors.push(...result.errors);
            warnings.push(...result.warnings);
        }
        return {
            success: errors.length === 0,
            data: customer,
            errors,
            warnings,
        };
    }
    mapToIncome(documents, customerId) {
        const errors = [];
        const warnings = [];
        const income = {
            byCustomer: [],
        };
        // Map IBL income
        if (documents["ibl-income"]) {
            const result = mapIblIncomeToIncome(documents["ibl-income"], customerId);
            if (result.income)
                income.byCustomer.push(result.income);
            errors.push(...result.errors);
            warnings.push(...result.warnings);
        }
        // Map income document
        if (documents["income-document"]) {
            const result = mapIncomeDocumentToIncome(documents["income-document"], customerId);
            if (result.income)
                income.byCustomer.push(result.income);
            errors.push(...result.errors);
            warnings.push(...result.warnings);
        }
        // Map pre-filled tax info income
        if (documents["pre-filled-tax-info"]) {
            const result = mapPreFilledTaxInfoToIncome(documents["pre-filled-tax-info"], customerId);
            if (result.income)
                income.byCustomer.push(result.income);
            errors.push(...result.errors);
            warnings.push(...result.warnings);
        }
        // Map pension document
        if (documents["pension-document"]) {
            const result = mapPensionDocumentToIncome(documents["pension-document"], customerId);
            if (result.income)
                income.byCustomer.push(result.income);
            errors.push(...result.errors);
            warnings.push(...result.warnings);
        }
        return {
            success: errors.length === 0,
            data: income,
            errors,
            warnings,
        };
    }
    mapToAssets(documents, customerId) {
        const errors = [];
        const warnings = [];
        const assets = { byCustomer: [] };
        // Map pre-filled tax info assets
        if (documents["pre-filled-tax-info"]) {
            const result = mapPreFilledTaxInfoToAssets(documents["pre-filled-tax-info"], customerId);
            if (result.assets)
                assets.byCustomer.push(result.assets);
            errors.push(...result.errors);
            warnings.push(...result.warnings);
        }
        return {
            success: errors.length === 0,
            data: assets,
            errors,
            warnings,
        };
    }
    mapToLiabilities(documents, customerId) {
        const errors = [];
        const warnings = [];
        const liabilities = { byCustomer: [] };
        // Map pre-filled tax info liabilities
        if (documents["pre-filled-tax-info"]) {
            const result = mapPreFilledTaxInfoToLiabilities(documents["pre-filled-tax-info"], customerId);
            if (result.liabilities)
                liabilities.byCustomer.push(result.liabilities);
            errors.push(...result.errors);
            warnings.push(...result.warnings);
        }
        if (documents.duo) {
            const result = mapDuoToLiabilities(documents.duo, customerId);
            if (result.liabilities)
                liabilities.byCustomer.push(result.liabilities);
            errors.push(...result.errors);
            warnings.push(...result.warnings);
        }
        return {
            success: errors.length === 0,
            data: liabilities,
            errors,
            warnings,
        };
    }
}
export function mergeDatakeeperWithDossierData(existingData, datakeeperData, customerId) {
    const mapper = new DatakeeperMapper();
    const { data: customer } = mapper.mapToCustomer(datakeeperData, customerId);
    const { data: income } = mapper.mapToIncome(datakeeperData, customerId);
    const { data: assets } = mapper.mapToAssets(datakeeperData, customerId);
    const { data: liabilities } = mapper.mapToLiabilities(datakeeperData, customerId);
    return {
        ...existingData,
        customers: mergeCustomers(existingData.customers, customer ? [customer] : []),
        income: income
            ? mergeIncome(existingData.income, income)
            : existingData.income,
        assets: assets
            ? mergeAssets(existingData.assets, assets)
            : existingData.assets,
        liabilities: liabilities
            ? mergeLiabilities(existingData.liabilities, liabilities)
            : existingData.liabilities,
    };
}
